import { useCallback } from 'react'

import type { CallLogFiltersInput } from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_VOICE_AGENT_HERO_STATS } from 'src/graphql/callAnaltyics.graphql'
import { HeroStatCard } from 'src/pages/AnalyticsPage/components/HeroStatCard'
import {
  formatCount,
  percentOf,
} from 'src/pages/AnalyticsPage/components/heroStatHelpers'

const formatAvgCallCount = (value: number | null | undefined): string => {
  if (value == null) return '—'
  return value.toFixed(1)
}

export const VoiceAgentHeroStrip = ({
  filters,
}: {
  filters: CallLogFiltersInput
}) => {
  const onError = useCallback(() => {
    toast('Failed to load voice agent summary', 'error')
  }, [])

  const { data, loading } = useQuery(GET_VOICE_AGENT_HERO_STATS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const stats = data?.voiceAgentHeroStats

  const outboundShare = percentOf(stats?.outboundCalls, stats?.totalCalls)
  const inboundShare = percentOf(stats?.inboundCalls, stats?.totalCalls)

  return (
    <div
      className="mb-4 grid grid-cols-5 gap-3"
      data-testid="voice-agent-hero-strip"
    >
      <HeroStatCard
        label="Total Voice Agent Calls"
        value={formatCount(stats?.totalCalls)}
        loading={loading}
        info={
          'All voice agent call attempts in the period (inbound + outbound).' +
          (stats
            ? `\n\n${formatCount(stats.inboundCalls)} inbound · ${formatCount(
                stats.outboundCalls
              )} outbound`
            : '')
        }
      />
      <HeroStatCard
        label="Outbound Voice Agent Calls"
        value={formatCount(stats?.outboundCalls)}
        loading={loading}
        info={
          'Voice agent calls we placed to candidates.' +
          (outboundShare
            ? `\n\n${outboundShare} of total voice agent calls`
            : '')
        }
      />
      <HeroStatCard
        label="Inbound Voice Agent Calls"
        value={formatCount(stats?.inboundCalls)}
        loading={loading}
        info={
          'Voice agent calls candidates initiated to us.' +
          (inboundShare ? `\n\n${inboundShare} of total voice agent calls` : '')
        }
      />
      <HeroStatCard
        label="Total Booked"
        value={formatCount(stats?.totalBookings)}
        loading={loading}
        info={'Interviews or meetings scheduled from outreach in the period.'}
      />
      <HeroStatCard
        label="Average Call Count per Booking"
        value={formatAvgCallCount(stats?.avgCallsPerBooking)}
        loading={loading}
        featured
        info="Average number of voice agent call attempts before a booked outcome."
      />
    </div>
  )
}
